import { useEffect, useState } from 'react';
import { Briefcase, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  useProfile,
  useUpdateCommercialStrategy,
} from '../hooks/use-dossier-company';
import { CpvPicker } from './cpv-picker';
import { EffectiveCpvDisplay } from './effective-cpv-display';

/**
 * Sprint 5b.1 v2 — Phase C.4b : section Stratégie commerciale en 4 blocs.
 *
 * Bloc 1 — Périmètre auto NAF (lecture seule, via EffectiveCpvDisplay)
 * Bloc 2 — Extensions (targetSectorsCpv)
 * Bloc 3 — Exclusions (excludedSectorsCpv)
 * Bloc 4 — Récapitulatif effectif (lecture seule, via EffectiveCpvDisplay)
 *
 * Sous-section Montants AO ciblés + acceptGroupements.
 */
export function CommercialStrategySection() {
  const { data } = useProfile();
  const profile = data?.profile ?? null;
  const update = useUpdateCommercialStrategy();

  const [targetSectorsCpv, setTargetSectorsCpv] = useState<string[]>([]);
  const [excludedSectorsCpv, setExcludedSectorsCpv] = useState<string[]>([]);
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [acceptGroupements, setAcceptGroupements] = useState(true);

  useEffect(() => {
    if (profile) {
      setTargetSectorsCpv(profile.targetSectorsCpv ?? []);
      setExcludedSectorsCpv(profile.excludedSectorsCpv ?? []);
      setMinAmount(profile.targetAoMinAmountEur ?? '');
      setMaxAmount(profile.targetAoMaxAmountEur ?? '');
      setAcceptGroupements(profile.acceptGroupements ?? true);
    }
  }, [profile]);

  async function handleSave() {
    const min = minAmount ? parseFloat(minAmount.replace(/\s/g, '').replace(',', '.')) : null;
    const max = maxAmount ? parseFloat(maxAmount.replace(/\s/g, '').replace(',', '.')) : null;
    await update.mutateAsync({
      targetSectorsCpv,
      excludedSectorsCpv,
      targetAoMinAmountEur: Number.isFinite(min) ? min : null,
      targetAoMaxAmountEur: Number.isFinite(max) ? max : null,
      acceptGroupements,
    });
  }

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Briefcase className="h-4 w-4" />
          Stratégie commerciale
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Périmètre métier automatique dérivé de votre NAF, complété par vos extensions et
          exclusions volontaires. Définit votre identité commerciale pour le sourcing d'AO et le scoring.
        </p>
      </div>

      {/* Blocs 1 + 4 (lecture seule, mis à jour automatiquement après save) */}
      <EffectiveCpvDisplay />

      <Separator />

      {/* Bloc 2 — Extensions */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Extensions volontaires</Label>
        <p className="text-xs text-muted-foreground">
          Codes CPV que vous voulez cibler <strong>en plus</strong> de votre périmètre métier
          automatique. Utile si vous diversifiez votre activité au-delà de votre NAF.
        </p>
        <CpvPicker
          value={targetSectorsCpv}
          onChange={setTargetSectorsCpv}
          placeholder="Ajouter un CPV à votre périmètre..."
          emptyTagsMessage="Aucune extension. Votre périmètre se limite au NAF auto."
          excludeCodes={excludedSectorsCpv}
        />
      </div>

      {/* Bloc 3 — Exclusions */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Exclusions</Label>
        <p className="text-xs text-muted-foreground">
          Codes CPV à exclure même s'ils sont dans votre périmètre automatique ou vos extensions.
        </p>
        <CpvPicker
          value={excludedSectorsCpv}
          onChange={setExcludedSectorsCpv}
          placeholder="Ajouter un CPV à exclure..."
          emptyTagsMessage="Aucune exclusion."
          excludeCodes={targetSectorsCpv}
        />
      </div>

      <Separator />

      {/* Montants AO ciblés */}
      <div>
        <Label className="text-sm font-medium">Montants AO ciblés</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="min-amount" className="text-xs">Minimum (€)</Label>
            <Input
              id="min-amount"
              type="text"
              inputMode="decimal"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              placeholder="ex. 10 000"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="max-amount" className="text-xs">Maximum (€)</Label>
            <Input
              id="max-amount"
              type="text"
              inputMode="decimal"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              placeholder="ex. 500 000"
            />
          </div>
        </div>
      </div>

      {/* acceptGroupements */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <Label htmlFor="accept-groupements" className="text-sm font-medium">
            Accepter les AO en groupement
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            Le CCP français autorise largement les groupements de candidats. Inclusif par défaut.
          </p>
        </div>
        <Switch
          id="accept-groupements"
          checked={acceptGroupements}
          onCheckedChange={setAcceptGroupements}
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={update.isPending}>
          {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Enregistrer la stratégie
        </Button>
      </div>
    </Card>
  );
}
